import React, { Component, Fragment } from "react";
import PropTypes from "prop-types";
import axios from "axios";
import utils from "@utils";
import { Link } from "react-router";
import {
	InvoiceEstimatesService,
	OrganizationService,
	ClientService,
	ProjectService,
	LocationService,
} from "@services";
import { ESTIMATE_TYPES_MAP, JOBS_SUB_MENU } from "@constants";
import { ReactSelect, HeaderGrid, TabMenu, ScrollDetect } from "../../../components";

import "./EstimatesList.scss";

let cancel;
let cancelCounts;

class EstimatesList extends Component {
	constructor(props) {
		super(props);

		const {
			router: {
				params,
				location: { query },
			},
		} = props;
		let status = "";
		// set initial status based on url params
		switch (params.viewType) {
			case "denied":
				status = 2;
				break;
			case "approved":
				status = 1;
				break;
			default:
				status = 3;
				break;
		}

		this.state = {
			estimatesResent: [],
			estimatesList: [],
			counts: {
				Approved: "",
				Denied: "",
				Pending: "",
			},
			filters: {
				agencyId: parseInt(query?.agencyId, 10) || "",
				clientId: parseInt(query?.clientId, 10) || "",
				projectId: parseInt(query?.projectId, 10) || "",
				locationId: parseInt(query?.locationId, 10) || "",
			},
			status,
			pageNumber: 1,
			agencies: [],
			clients: [],
			projects: [],
			locations: [],
			countsLoading: true,
			agenciesLoading: false,
			clientsLoading: false,
			projectsLoading: false,
			locationsLoading: false,
			estimateSending: "",
			hasMore: true,
		};
	}

	componentDidMount() {
		this.getCounts();
		this.getAgencies();
		this.getClients();
	}

	/** Retrieves a paginated list of estimates for the current user */
	getJobEstimates = () => {
		const { lang } = global;
		const {
			status,
			pageNumber,
			filters: { agencyId, clientId, projectId, locationId },
		} = this.state;

		const params = {
			status,
			agencyId,
			clientId,
			projectId,
			locationId,
			pageNumber,
			displayCount: 25,
		};

		// If there is a request already running, abort it and start a new one
		if (cancel) {
			cancel();
		}

		InvoiceEstimatesService.getAll({
			...params,
			getCancelToken: c => {
				cancel = c;
			},
		})
			.then(({ data }) => {
				cancel = undefined;
				this.setState(prevState => ({
					estimatesList:
						pageNumber > 1
							? [...prevState.estimatesList, ...data.ItemList]
							: data.ItemList,
					hasMore: data.ItemList.length >= data.DisplayCount,
					pageNumber: prevState.pageNumber + 1,
				}));
			})
			.catch(error => {
				if (!axios.isCancel(error)) {
					this.setState({
						estimatesList: [],
						pageNumber: 1,
						hasMore: false,
					});
					utils.notify({
						type: "error",
						message: lang.t("Error loading jobs"),
						timeOut: 1500,
					});
				}
			});
	};

	/** Retrieves the number of estimates that are in each status */
	getCounts = () => {
		const { lang } = global;
		const {
			filters: { agencyId, clientId, projectId, locationId },
		} = this.state;

		const params = {
			agencyId,
			clientId,
			projectId,
			locationId,
		};

		// If there is a request already running, abort it and start a new one
		if (cancelCounts) {
			cancelCounts();
		}
		// Remove empty query params from request
		const filterParams = Object.fromEntries(
			Object.entries(params).filter(([, value]) => value !== null && value !== ""),
		);

		InvoiceEstimatesService.getCounts({
			...filterParams,
			getCancelToken: c => {
				cancelCounts = c;
			},
		})
			.then(({ data }) => {
				cancelCounts = undefined;
				this.setState({ counts: data, countsLoading: false });
			})
			.catch(error => {
				this.setState({ countsLoading: false });
				if (!axios.isCancel(error)) {
					utils.notify({
						type: "error",
						message: lang.t("Error loading Job Counts"),
						timeOut: 1500,
					});
				}
			});
	};

	/** Retrieves a list of agencies that can be used to filter estimates */
	getAgencies = () => {
		const { lang } = global;
		const { user } = this.props;

		/**
		 * Agencies only needs to be
		 * requested for Super Admin or LEA
		 * users
		 */
		if (!user.isSuperAdmin() && !user.isLEA()) {
			return;
		}

		this.setState({ agenciesLoading: true });

		OrganizationService.getAll({ minimalData: true })
			.then(({ data }) => {
				const formattedAgencies = data.map(({ Id: value, Name: label }) => ({
					value,
					label,
				}));
				this.setState({ agencies: formattedAgencies });
			})
			.catch(() => {
				utils.notify({
					type: "error",
					message: lang.t("Error loading Agencies"),
					timeOut: 1500,
				});
			})
			.finally(() => {
				this.setState({ agenciesLoading: false });
			});
	};

	/** Retrieves a list of clients that can be used to filter estimates */
	getClients = () => {
		const { lang } = global;
		const { user } = this.props;
		const {
			filters: { includeInactive, agencyId: organizationId },
		} = this.state;

		/**
		 * If the current user is
		 * Super Admin or LEA, and there
		 * is no selected organizationId
		 * stop propagation
		 */
		if ((user.isSuperAdmin() || user.isLEA()) && organizationId === 0) {
			return;
		}
		this.setState({ clientsLoading: true });

		ClientService.getAll({ includeInactive, organizationId })
			.then(({ data }) => {
				const formattedClients = data.map(({ Id: value, Name: label }) => ({
					value,
					label,
				}));
				this.setState({ clients: formattedClients });
			})
			.catch(() => {
				utils.notify({
					type: "error",
					message: lang.t("Error loading clients"),
					timeOut: 1500,
				});
			})
			.finally(() => {
				this.setState({ clientsLoading: false });
			});
	};

	/** Retrieves a list of projects that can be used to filter estimates */
	getProjects = () => {
		const { lang } = global;
		const { user } = this.props;
		const {
			filters: { clientId, includeInactive },
		} = this.state;

		/**
		 * If the current user is
		 * Super Admin or LEA, and there
		 * is no selected clientId
		 * stop propagation
		 */
		if ((user.isSuperAdmin() || user.isLEA()) && !clientId) {
			return;
		}

		this.setState({ projectsLoading: true, locations: [], projects: [] });

		ProjectService.getAll({ includeAll: includeInactive, clientId })
			.then(({ data: { ItemList } }) => {
				const formattedProjects = ItemList.map(({ Id: value, Name: label }) => ({
					value,
					label,
				}));
				this.setState({ projects: formattedProjects });
			})
			.catch(() => {
				utils.notify({
					type: "error",
					message: lang.t("Error loading project list"),
					timeOut: 1500,
				});
			})
			.finally(() => {
				this.setState({ projectsLoading: false });
			});
	};

	/** Retrieves a list of locations that can be used to filter estimates */
	getLocations = () => {
		const { lang } = global;
		const { user } = this.props;
		const {
			filters: { clientId, projectId },
		} = this.state;

		let id = projectId;
		let service = LocationService;
		let endpoint = "getAll";

		if (user.isCoordinator()) {
			id = clientId;
			service = ClientService;
			endpoint = "getLocations";
		}

		if (!id) {
			return;
		}

		this.setState({ locationsLoading: true, locations: [] });
		const params = endpoint === "getAll" ? { projectId: id } : id;
		service[endpoint](params)
			.then(({ data }) => {
				const formattedLocations = data.map(({ Id: value, Name: label }) => ({
					value,
					label,
				}));
				this.setState({ locations: formattedLocations });
			})
			.catch(() => {
				utils.notify({
					type: "error",
					message: lang.t("Error loading Locations"),
					timeOut: 1500,
				});
			})
			.finally(() => {
				this.setState({ locationsLoading: false });
			});
	};

	/**
	 * Re-sends an estimate to a client
	 * @param {Number} estimateId The ID number for the estimate to be re-sent
	 */
	resendEstimate = estimateId => () => {
		const { lang } = global;
		const { estimatesResent } = this.state;
		const filterId = id => estimatesResent.filter(estimate => estimate !== id);

		this.setState({
			estimatesResent: [...estimatesResent, estimateId],
			estimateSending: estimateId,
		});

		InvoiceEstimatesService.resend(estimateId)
			.then(() => {
				this.setState({ estimatesResent: filterId(estimateId), estimateSending: "" });
				utils.notify({
					type: "success",
					message: lang.t("Invoice Estimate resent successfully"),
					timeOut: 1500,
				});
			})
			.catch(() => {
				this.setState({ estimatesResent: filterId(estimateId), estimateSending: "" });
				utils.notify({
					type: "error",
					message: lang.t("Error resending estimate"),
					timeOut: 1500,
				});
			});
	};

	/** Build and update a URL to reflect the page's current filters */
	updateUrl = () => {
		const { router } = this.props;
		const { status, filters } = this.state;
		// takes in the filters state object and converts them into a string of params whose values are not empty strings
		const queryObject = Object.fromEntries(
			Object.entries(filters).filter(([, val]) => val !== ""),
		);
		const queryParams = new URLSearchParams(queryObject).toString();
		let active;
		switch (status) {
			case 2:
				active = "denied";
				break;
			case 1:
				active = "approved";
				break;
			default:
				active = "pending";
				break;
		}
		router.push(`/jobs/estimates/${active}${queryParams === "" ? "" : `?${queryParams}`}`);
	};

	/**
	 * Changes the type of job approvals shown in the list.
	 * After updating approval type, {@link getCounts} & {@link updateUrl} are automatically called.
	 * @param {*} status A value indicating the estimate type to view
	 */
	setActiveGrid = status => {
		const estimateType = ESTIMATE_TYPES_MAP.find(
			estimate => estimate.type === parseInt(status, 10),
		);

		this.setState(
			{
				hasMore: true,
				estimatesList: [],
				pageNumber: 1,
				status: estimateType?.type || 3,
			},
			() => {
				this.getCounts();
				this.getJobEstimates();
				this.updateUrl();
			},
		);
	};

	/**
	 * Renders a label with appropriate text and color to visually represent the status of an estimate
	 * @param {Number} statusId A value indicating the status of an estimate
	 * @returns {Object} an object containing `title` and `class` properties used to display a label
	 */
	renderStatusLabel = statusId => {
		const { lang } = global;
		const label = {};

		switch (statusId) {
			case 0:
				label.title = lang.t("Expired");
				label.class = "label-warning";
				break;
			case 1:
				label.title = lang.t("Approved");
				label.class = "label-success";

				break;
			case 2:
				label.title = lang.t("Denied");
				label.class = "label-danger";
				break;
			default:
				label.title = lang.t("Pending");
				label.class = "label-warning";
				break;
		}

		return label;
	};

	/**
	 * Handles changes to agency and client filters. After updating the selection, {@link getCounts} & {@link updateUrl} are always called.
	 *
	 * Depending on which property was changed as well as other conditions, other methods may be called as well.
	 * @param {Object} e The object returned by `ReactSelect` containing `value` and `label` properties
	 * @param {String} name The name of the property to update
	 */
	handleOnSelect = (e, name) => {
		const { user } = this.props;
		if (!e) {
			this.setState(
				prevState => ({
					hasMore: true,
					pageNumber: 1,
					projects: [],
					locations: [],
					filters: {
						agencyId: name === "agencyId" ? "" : prevState.filters.agencyId,
						clientId: "",
						projectId: "",
						locationId: "",
					},
				}),
				() => {
					this.getCounts();
					this.updateUrl();
					if (name === "agencyId") {
						this.getClients();
					}
				},
			);
		} else {
			const { value } = e;
			this.setState(
				prevState => ({
					estimatesList: [],
					hasMore: true,
					pageNumber: 1,
					filters: {
						...prevState.filters,
						[name]: value,
					},
				}),
				() => {
					if (name === "agencyId") {
						this.getClients();
					} else if (name === "clientId" && user.isCoordinator()) {
						this.getLocations();
					} else if (name === "clientId") {
						this.getProjects();
					}
					this.getCounts();
					this.updateUrl();
				},
			);
		}
	};

	/**
	 * Handles changes to the project filter. After updating the selection, {@link getCounts} & {@link updateUrl} are always called.
	 *
	 * If a new project was selected, {@link getLocations} will be called as well.
	 * @param {Object} e The object returned by `ReactSelect` containing `value` and `label` properties
	 */
	handleProjectChange = e => {
		if (!e) {
			this.setState(
				prevState => ({
					hasMore: true,
					pageNumber: 1,
					locations: [],
					filters: {
						...prevState.filters,
						projectId: "",
						locationId: "",
					},
				}),
				() => {
					this.getCounts();
					this.updateUrl();
				},
			);
		} else {
			const { value } = e;
			this.setState(
				prevState => ({
					estimatesList: [],
					hasMore: true,
					pageNumber: 1,
					filters: {
						...prevState.filters,
						projectId: value,
					},
				}),
				() => {
					this.getCounts();
					this.getLocations();
					this.updateUrl();
				},
			);
		}
	};

	/**
	 * Handles changes to the location filter. After updating the selection, {@link getCounts} & {@link updateUrl} are always called.
	 *
	 * @param {Object} e The object returned by `ReactSelect` containing `value` and `label` properties
	 */
	handleLocationChange = e => {
		if (!e) {
			this.setState(
				prevState => ({
					hasMore: true,
					pageNumber: 1,
					filters: {
						...prevState.filters,
						locationId: "",
					},
				}),
				() => {
					this.getCounts();
					this.updateUrl();
				},
			);
		} else {
			const { value } = e;
			this.setState(
				prevState => ({
					estimatesList: [],
					hasMore: true,
					pageNumber: 1,
					filters: {
						...prevState.filters,
						locationId: value,
					},
				}),
				() => {
					this.getCounts();
					this.updateUrl();
				},
			);
		}
	};

	onNavigate = (estimateId, e) => {
		const { router } = this.props;
		if (e.target.name !== "resend-button") {
			router.push(`/jobs/estimates/detail/${estimateId}`);
		}
	};

	onClickTab = ({ tabNumber, url }) => {
		const {
			router: { push },
		} = this.props;
		const thisTab = JOBS_SUB_MENU.find(tab => tab.type === "estimates");
		if (tabNumber !== thisTab.tabNumber) push(url);
	};

	render() {
		const { lang } = global;
		const { user } = this.props;
		const {
			estimatesResent,
			estimatesList,
			counts,
			agencies,
			clients,
			projects,
			locations,
			countsLoading,
			agenciesLoading,
			clientsLoading,
			projectsLoading,
			locationsLoading,
			estimateSending,
			hasMore,
			status,
			filters: { agencyId, clientId, projectId, locationId },
		} = this.state;

		return (
			<Fragment>
				<TabMenu
					tabs={JOBS_SUB_MENU}
					activeTab={JOBS_SUB_MENU.find(tab => tab.type === "estimates").tabNumber}
					onClickTab={this.onClickTab}
					user={user}
				/>
				<div className="estimates-list">
					<div className="container">
						{/* Header */}
						<div className="estimate-header">
							<div className="row">
								<div className="col-xs-6 col-sm-10">
									<h1 className="text-center">{lang.t("Estimates")}</h1>
								</div>
								<If
									condition={
										!user.isSuperAdmin() && !user.isClient() && !user.IsViewOnly
									}
								>
									<div className="col-xs-6 col-sm-2">
										<Link
											name="add-job"
											data-test-id="add-job"
											to="/jobs/add"
											className="btn btn-primary btn-block"
										>
											{`+ ${lang.t("New Job")}`}
										</Link>
									</div>
								</If>
							</div>
						</div>
						{/* Header */}

						{/* Active Grid */}
						<div className="estimate-grid">
							<HeaderGrid
								gridOptions={ESTIMATE_TYPES_MAP}
								loadingCounts={countsLoading}
								counts={counts}
								activeGrid={status}
								setActive={this.setActiveGrid}
							/>
						</div>
						{/* Active Grid */}

						{/* Filters */}
						<div className="estimate-filters">
							<div className="row">
								<div className="col-xs-12">
									<h2>{lang.t("Filters")}</h2>
								</div>
								<If condition={user.isSuperAdmin() || user.isLEA()}>
									<div className="col-sm-3 form-group">
										<label htmlFor="select-agency">{lang.t("Agency")}</label>
										<ReactSelect
											id="select-agency"
											name="select-agency"
											options={agencies}
											value={agencyId}
											onChange={e => this.handleOnSelect(e, "agencyId")}
											isLoading={agenciesLoading}
											isClearable
											dataTestId="estimate__select-agency"
										/>
									</div>
								</If>
								<If condition={user.isSuperAdmin() || !user.isClient()}>
									<div className="col-sm-3 form-group">
										<label htmlFor="select-client">{lang.t("Client")}</label>
										<ReactSelect
											id="select-client"
											name="select-client"
											value={clientId}
											options={clients}
											onChange={e => this.handleOnSelect(e, "clientId")}
											isLoading={clientsLoading}
											isClearable
											dataTestId="estimate__select-client"
										/>
									</div>
								</If>
								<If condition={!user.isCoordinator()}>
									<div className="col-sm-3 form-group">
										<label htmlFor="select-project">{lang.t("Project")}</label>
										<ReactSelect
											id="select-project"
											name="select-project"
											value={projectId}
											options={projects}
											onChange={this.handleProjectChange}
											isLoading={projectsLoading}
											isClearable
											dataTestId="estimate__select-project"
										/>
									</div>
								</If>
								<div className="col-sm-3 form-group">
									<label htmlFor="select-location">{lang.t("Location")}</label>
									<ReactSelect
										id="select-location"
										name="select-location"
										value={locationId}
										options={locations}
										onChange={this.handleLocationChange}
										isLoading={locationsLoading}
										isClearable
										dataTestId="estimate__select-location"
									/>
								</div>
							</div>
						</div>
						{/* Filters */}

						{/* List */}
						<div className="estimate-table">
							<div className="list-header hidden-xs">
								<div className="row">
									<div className="col-sm-2">
										<strong>{lang.t("Created By")}</strong>
									</div>
									<div className="col-sm-3">
										<strong>{lang.t("Job")}</strong>
									</div>
									<div className="col-sm-3">
										<strong>{lang.t("Client")}</strong>
									</div>
									<div className="col-sm-1 text-right">
										<strong>{lang.t("Amount")}</strong>
									</div>
									<If condition={status === 2}>
										<div className="col-sm-3">
											<strong>{lang.t("Reason")}</strong>
										</div>
									</If>
								</div>
							</div>
							<div className="list-body">
								<For
									each="estimate"
									of={estimatesList}
									index="index"
								>
									<div
										className="row v-align-sm"
										role="button"
										key={`job-estimate_${estimate.Id}`}
										data-test-id={`estimate__row_${estimate.Id}`}
										tabIndex={0}
										onClick={e => this.onNavigate(estimate.Id, e)}
										onKeyPress={e => {
											if (e.key === "Enter") {
												this.onNavigate(estimate.Id, e);
											}
										}}
									>
										<div className="col-xs-12 col-sm-2">
											<div>
												<strong className="visible-xs">
													{lang.t("Created By")}
												</strong>
												{estimate.AgencyContactName}
												<br />
												{utils.formatDate(
													new Date(`${estimate.CreateDateUTC}Z`),
													"en-US",
													user.TimeZoneId,
													"2-digit",
												)}
											</div>
											<br className="visible-xs" />
										</div>
										<div className="col-xs-12 col-sm-3">
											<strong className="visible-xs">{lang.t("Job")}</strong>
											<div className="truncate-text" data-test-id="estimate__job-event-name">
												{estimate.JobEventName}
											</div>
											<div>
												{utils.formatDate(
													new Date(`${estimate.JobEventStartDateTime}Z`),
													"en-US",
													user.TimeZoneId,
													"2-digit",
												)}
											</div>
											<br className="visible-xs" />
										</div>
										<div className="col-xs-12 col-sm-3">
											<div className="truncate-text" data-test-id="estimate__client-name">
												{estimate.ClientName}
											</div>
											<div
												className="truncate-text"
												title={estimate.SentTo}
												data-test-id="estimate__sent-to"
											>
												{estimate.SentTo}
											</div>
											<br className="visible-xs" />
										</div>
										<div className="col-xs-6 col-xs-push-6 col-sm-push-0 col-sm-1 text-right">
											<div>
												<strong className="visible-xs-inline">
													{lang.t("Amount")}:{" "}
												</strong>
												{utils.currencyFormatter(estimate.Total)}
											</div>
											<If condition={estimate.RequiresPrepayment}>
												<span
													className="label label-success"
													title={lang.t("Requires prepayment")}
												>
													{lang.t("Prepay")}
												</span>
											</If>
											<If condition={status !== 3}>
												<span
													className={`label ${this.renderStatusLabel(estimate.Status).class}`}
												>
													{this.renderStatusLabel(estimate.Status).title}
												</span>
											</If>
											<br className="visible-xs" />
										</div>
										<If condition={status === 2}>
											<div className="col-xs-6 col-xs-pull-6 col-sm-pull-0  col-sm-3">
												{estimate.Note ?? ""}
											</div>
										</If>
										<If condition={status === 3}>
											<div className="col-xs-12 col-sm-2 col-sm-offset-1">
												<button
													type="button"
													name="resend-button"
													className="btn btn-primary btn-block"
													disabled={
														estimatesResent.includes(estimate.Id) ||
														user.IsViewOnly
													}
													onClick={this.resendEstimate(estimate.Id)}
												>
													<Choose>
														<When
															condition={
																estimateSending === estimate.Id
															}
														>
															<i className="glyphicon glyphicon-repeat rkf-spin rkf-fw" />
														</When>
														<Otherwise>{lang.t("Resend")}</Otherwise>
													</Choose>
												</button>
											</div>
										</If>
									</div>
								</For>
								<If condition={hasMore}>
									<ScrollDetect
										className="text-center"
										onVisible={this.getJobEstimates}
									>
										<i className="glyphicon glyphicon-repeat rkf-spin rkf-fw" />
									</ScrollDetect>
								</If>
							</div>
						</div>
						{/* List */}
					</div>
				</div>
			</Fragment>
		);
	}
}

EstimatesList.propTypes = {
	user: PropTypes.object,
	router: PropTypes.object,
};

EstimatesList.defaultProps = {
	user: {},
	router: {},
};

export default EstimatesList;
